/**
 * Taskbar.jsx — Bottom taskbar, modelled 1:1 on a modern Windows taskbar.
 *
 * Left:   themed window-style Start button (opens the HomeMenu).
 * Centre: one button per OPEN window on the active dashboard (#4/#7). The
 *         focused window reads as active; minimised windows are dimmed.
 *         Clicking toggles minimise / restore / raise (#336, #339 reworked).
 * Right:  a system tray — auto-organize button (#8) and the Jone/Jode twin
 *         moon clock (#8), mirroring the Windows clock at the far right.
 *
 * When taskbarStore.alwaysVisible() is true (default) the bar is always
 * shown. In auto-hide mode it only appears while a window is minimised.
 */
import { For, Show, createSignal, onMount, onCleanup } from 'solid-js';
import wm from '../lib/wmStore.js';
import { EMOJI } from '../lib/emoji.js';
import taskbarStore from '../lib/taskbarStore.js';
import { homeMenu } from '../lib/homeMenuStore.js';
import { getESOTime } from '../lib/esoTime.js';
import { glassChromeHidden, restoreGlassChromeSettings } from '../lib/glassMode.js';
import Tooltip from './MouseTooltip.jsx';
import ContextMenu from './ContextMenu.jsx';
import './Taskbar.css';

// A clean 4-pane "window" glyph that inherits the theme accent via
// currentColor — the graphical Start button requested in issue #3.
function StartGlyph() {
  return (
    <svg class="v2-taskbar__start-icon" viewBox="0 0 16 16" width="18" height="18"
      fill="none" stroke="currentColor" stroke-width="1.3" aria-hidden="true">
      <rect x="1.6" y="1.6" width="12.8" height="12.8" rx="2.2" />
      <line x1="8" y1="1.6" x2="8" y2="14.4" />
      <line x1="1.6" y1="8" x2="14.4" y2="8" />
    </svg>
  );
}

export default function Taskbar() {
  const [context, setContext] = createSignal(null);
  const dash = () => wm.state.dashboards.find(d => d.id === wm.state.activeDashboardId);
  // #4/#7 — every open window appears on the bar (not just minimised ones).
  const windows = () => dash()?.widgets || [];
  const minimized = () => windows().filter(w => w.minimized);
  const focusedId = () => {
    const order = wm.state.focusOrder;
    return order.length ? order[order.length - 1] : null;
  };
  const visible = () => taskbarStore.alwaysVisible() || minimized().length > 0;

  function openWindowMenu(e, widget) {
    e.preventDefault();
    e.stopPropagation();
    setContext({ x: e.clientX, y: e.clientY, widgetId: widget.id });
  }

  function openWindowMenuFromKeyboard(e, widget) {
    if (e.key !== 'ContextMenu' && !(e.shiftKey && e.key === 'F10')) return;
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    setContext({ x: rect.left + 18, y: rect.top - 8, widgetId: widget.id });
  }

  function contextWidget() {
    const id = context()?.widgetId;
    return windows().find(w => w.id === id) || null;
  }

  function openWidgetSettings(widget) {
    if (!widget || typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent('v2-open-window-settings', { detail: { widgetId: widget.id } }));
  }

  function restoreGlassChrome(widget) {
    if (!widget) return;
    wm.setSettings(widget.id, restoreGlassChromeSettings(widget.settings || {}));
  }

  function contextItems(widget) {
    if (!widget) return [];
    return [
      {
        icon: widget.minimized ? '↗' : '◉',
        label: widget.minimized ? 'Restore and focus' : 'Focus window',
        onSelect: () => widget.minimized ? wm.activateWindow(widget.id) : wm.focus(widget.id),
      },
      { icon: EMOJI.settings, label: 'Settings', iconTone: 'theme', onSelect: () => openWidgetSettings(widget) },
      ...glassChromeHidden(widget) ? [{
        icon: '↺',
        label: 'Restore border and controls',
        onSelect: () => restoreGlassChrome(widget),
      }] : [],
      {
        icon: '▁',
        label: 'Minimize',
        disabled: !!widget.minimized,
        onSelect: () => wm.requestMinimize(widget.id),
      },
      {
        icon: widget.maximized ? '◱' : '□',
        label: widget.maximized ? 'Restore size' : 'Maximize',
        onSelect: () => wm.toggleMaximize(widget.id),
      },
      { separator: true },
      {
        icon: widget._permanent ? '◆' : '×',
        label: widget._permanent ? 'Pinned window' : 'Close window',
        disabled: !!widget._permanent,
        danger: !widget._permanent,
        onSelect: () => wm.closeWidget(widget.id),
      },
    ];
  }

  return (
    <Show when={visible()}>
      <div class="v2-taskbar">
        {/* Start button — graphical, theme-aware (#3) */}
        <button
          class="v2-taskbar__start"
          type="button"
          title="Home menu"
          aria-label="Open home menu"
          classList={{ 'is-open': homeMenu.open() }}
          data-hint={'Open the home menu.\nWidgets · Theme · Settings · Account · Layout · Desks'}
          data-hint-level="critical"
          onClick={() => homeMenu.toggle()}
        >
          <StartGlyph />
        </button>

        {/* Open-window buttons (#4/#7) */}
        <div class="v2-taskbar__windows">
          <For each={windows()}>{(w) => {
            const meta = () => wm.state.catalog.find(c => c.type === w.widget_type);
            return (
              <button
                class="v2-taskbar__win"
                data-wid={w.id}
                classList={{
                  'is-active': focusedId() === w.id && !w.minimized,
                  'is-minimized': !!w.minimized,
                }}
                title={meta()?.title || w.widget_type}
                onClick={() => wm.activateWindow(w.id)}
                onContextMenu={(e) => openWindowMenu(e, w)}
                onKeyDown={(e) => openWindowMenuFromKeyboard(e, w)}
              >
                <span class="v2-taskbar__emoji">{meta()?.emoji || EMOJI.window}</span>
                <span class="v2-taskbar__label">{meta()?.title || w.widget_type}</span>
              </button>
            );
          }}</For>
        </div>

        {/* System tray — auto-organize + twin-moon clock (#8) */}
        <div class="v2-taskbar__tray">
          <Show when={windows().length > 0}>
            <Tooltip text={'Auto-organize — tile every visible window into a tidy grid.'}>
              <button
                class="v2-taskbar__tray-btn"
                type="button"
                aria-label="Auto-organize windows"
                data-hint={'Auto-organize every visible window into a grid.\nMinimised windows stay put.'}
                onClick={() => wm.arrangeTile()}
              >▦</button>
            </Tooltip>
          </Show>
          <TaskbarRelay />
          <TaskbarClock />
        </div>

        <Show when={context() && contextWidget()}>
          <ContextMenu
            open={!!context()}
            x={context().x}
            y={context().y}
            label="Taskbar window actions"
            items={contextItems(contextWidget())}
            onClose={() => setContext(null)}
          />
        </Show>
      </div>
    </Show>
  );
}

// Fissal Relay status beacon: compact enough for the tray, but deliberately
// more alive than a static account link so the contribution path stays visible.
function TaskbarRelay() {
  const [me, setMe] = createSignal(null);
  const [open, setOpen] = createSignal(false);
  const [downloadInfo, setDownloadInfo] = createSignal(null);
  let rootRef;

  async function refresh() {
    if (!wm.state.viewer) {
      setMe(null);
      return;
    }
    try {
      const response = await fetch('/auth/me', { credentials: 'include' });
      setMe(response.ok ? await response.json() : null);
    } catch { setMe(null); }
  }

  async function fetchDownloadInfo() {
    try {
      const response = await fetch('/api/relay/v1/download');
      if (response.ok) setDownloadInfo(await response.json());
    } catch { /* best effort */ }
  }

  function openSyncData() {
    setOpen(false);
    if (me()?.relayLinked) wm.addWidget('sync-points');
    else window.dispatchEvent(new CustomEvent('v2-open-relay-setup'));
  }

  function openRelaySetup() {
    setOpen(false);
    window.dispatchEvent(new CustomEvent('v2-open-relay-setup'));
  }

  onMount(() => {
    refresh();
    fetchDownloadInfo();
    const interval = setInterval(refresh, 30_000);
    const onDocPointerDown = (event) => {
      if (open() && rootRef && !rootRef.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => { if (open() && event.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', onDocPointerDown, true);
    document.addEventListener('keydown', onKey);
    onCleanup(() => {
      clearInterval(interval);
      document.removeEventListener('pointerdown', onDocPointerDown, true);
      document.removeEventListener('keydown', onKey);
    });
  });

  const linked = () => !!me()?.relayLinked;
  const lastSeen = () => me()?.relayLastSeen ? new Date(me().relayLastSeen).toLocaleString() : 'Awaiting first signal';
  const label = () => linked() ? 'Relay linked' : 'Help power the market';
  const clientVersion = () => downloadInfo()?.clientVersion || '1.7.0';
  const clientUrl = () => downloadInfo()?.clientUrl || 'https://github.com/EchoTiger/ESOSyncClient/releases/latest/download/RedfurSync.exe';
  const addonUrl = () => downloadInfo()?.addonUrl || '/api/relay/v1/download-addon';

  return (
    <div class="v2-taskbar__relay-wrap" ref={el => rootRef = el}>
      <button
        class="v2-taskbar__relay"
        classList={{ 'is-linked': linked(), 'is-open': open() }}
        type="button"
        aria-label={label()}
        aria-expanded={open()}
        data-hint={`${label()}
Click to ${linked() ? 'open Your Sync Data' : 'download and pair Fissal Relay'}.`}
        onClick={() => setOpen(value => !value)}
      >
        <span class="v2-taskbar__relay-signal" aria-hidden="true"><i /><i /><i /></span>
        <span class="v2-taskbar__relay-label">{linked() ? 'RELAY' : 'SYNC'}</span>
      </button>
      <Show when={open()}>
        <div class="v2-taskbar__relay-pop" role="dialog" aria-label="Fissal Relay status">
          <div class="v2-relay-pop__scan" aria-hidden="true"><span /><span /><span /><span /></div>
          <div class="v2-relay-pop__heading">
            <span class="v2-relay-pop__eyebrow">FISSAL RELAY</span>
            <strong>{linked() ? 'Your market signal is live' : 'Help power the market'}</strong>
          </div>
          <p class="v2-relay-pop__copy">
            {linked()
              ? 'Your desktop relay is sending anonymous trade data back to the guild market.'
              : 'Download the desktop relay to contribute trade data while you play ESO.'}
          </p>
          <div class="v2-relay-pop__stats">
            <span><b>{linked() ? 'CONNECTED' : 'OFFLINE'}</b><small>{linked() ? me().relayDisplayName || 'Desktop relay' : 'Desktop relay needed'}</small></span>
            <span><b>{linked() ? me().relayDeviceCount || 1 : '—'}</b><small>{linked() ? 'paired device' : 'sync points'}</small></span>
          </div>
          <Show when={linked()}>
            <div class="v2-relay-pop__last">Last signal: {lastSeen()}</div>
          </Show>
          <div class="v2-relay-pop__actions">
            <a
              class="v2-relay-pop__download"
              href={clientUrl()}
              title={`Download Windows Relay Executable (v${clientVersion()})`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span>⚡ Download Windows Relay (.exe)</span>
              <small class="v2-relay-pop__version">v{clientVersion()}</small>
            </a>
            <Show when={linked()} fallback={
              <button class="v2-relay-pop__open v2-relay-pop__open--sub" type="button" onClick={openRelaySetup}>
                Pair Device / Settings
              </button>
            }>
              <button class="v2-relay-pop__open" type="button" onClick={openSyncData}>
                Open Your Sync Data
              </button>
            </Show>
          </div>
          <div class="v2-relay-pop__note">
            <span>Direct executable hosted on GitHub. Automatically installs in-game AddOn.</span>
            <span class="v2-relay-pop__subnote">
              Manual <a href={addonUrl()} download>Lua AddOn zip</a> is optional.
            </span>
          </div>
        </div>
      </Show>
    </div>
  );
}

// ── Jone & Jode twin-moon clock (#8) ─────────────────────────────────────
// Compact analogue of the Windows tray clock: the two ESO moons + the
// in-game time. Clicking opens the full ESO clock widget. Refreshes every
// 3 real seconds (≈ one ESO minute).
const MOON_NAMES = {
  new: 'New', waxingCrescent: 'Waxing Crescent', firstQuarter: 'First Quarter',
  waxingGibbous: 'Waxing Gibbous', full: 'Full', waningGibbous: 'Waning Gibbous',
  thirdQuarter: 'Third Quarter', waningCrescent: 'Waning Crescent',
};

function TaskbarClock() {
  const [eso, setEso] = createSignal(getESOTime());
  const [open, setOpen] = createSignal(false);
  let rootRef;
  onMount(() => {
    const id = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      setEso(getESOTime());
    }, 3_000);
    const onVis = () => {
      if (typeof document !== 'undefined' && !document.hidden) setEso(getESOTime());
    };
    document.addEventListener('visibilitychange', onVis);
    onCleanup(() => document.removeEventListener('visibilitychange', onVis));
    const onDocPointerDown = (e) => {
      if (!open()) return;
      if (rootRef && rootRef.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (open() && e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onDocPointerDown, true);
    document.addEventListener('keydown', onKey);
    onCleanup(() => clearInterval(id));
    onCleanup(() => {
      document.removeEventListener('pointerdown', onDocPointerDown, true);
      document.removeEventListener('keydown', onKey);
    });
  });
  const commentary = () => {
    const t = eso();
    const lines = {
      dawn: 'First light over Tamriel. Fissal is calibrating lenses and counting last night\'s quiet sales.',
      morning: 'The market is awake. Jode and Jone make fine accountants when the ledgers are honest.',
      afternoon: 'Bright hours, busy kiosks. Watch the undercuts before they bite your margins.',
      dusk: 'Lanterns up. A good hour to relist the treasures that deserve theatre.',
      night: 'The moons are working late. Fissal approves of celestial machinery with good timing.',
    };
    return lines[t.timeOfDay] || lines.night;
  };
  const bloodmoonActive = () => {
    const t = eso();
    return !t.isDay && Number(t.esoJodeIlluminationPercent) >= 70;
  };
  return (
    <div class="v2-taskbar__clock-wrap" ref={el => rootRef = el}>
      <button
        class="v2-taskbar__clock"
        type="button"
        aria-label="ESO time and moons"
        aria-expanded={open()}
        data-hint={'ESO in-game time.\nJode (big moon) & Jone (little moon) phases.\nClick to open the full clock.'}
        onClick={() => setOpen(v => !v)}
      >
        <span class="v2-taskbar__moons" aria-hidden="true">
          <span class="v2-taskbar__moon v2-taskbar__moon--jode" style={{ '--lit': `${eso().esoJodeIlluminationPercent}%` }} />
          <span class="v2-taskbar__moon v2-taskbar__moon--jone" style={{ '--lit': `${eso().esoJoneIlluminationPercent}%` }} />
        </span>
        <span class="v2-taskbar__clock-time">{eso().timeString}</span>
      </button>
      <Show when={open()}>
        <div class="v2-taskbar__moon-pop" role="dialog" aria-label="Jone and Jode moon details">
          <div class="v2-moon-pop__sky" aria-hidden="true">
            <MoonOrb kind="jode" pct={eso().esoJodeIlluminationPercent} label="Jode" />
            <MoonOrb kind="jone" pct={eso().esoJoneIlluminationPercent} label="Jone" />
          </div>
          <div class="v2-moon-pop__date">{eso().timeString} · {eso().dateString}</div>
          <div class="v2-moon-pop__comment">{commentary()}</div>
          <div class="v2-moon-pop__grid">
            <MoonStat title="ESO synced" jode={eso().esoJodeIlluminationPercent} jone={eso().esoJoneIlluminationPercent}
              jodePhase={eso().jodePhase} jonePhase={eso().jodePhase} />
            <MoonStat title="Lore cycles" jode={eso().loreJodeIlluminationPercent} jone={eso().loreJoneIlluminationPercent}
              jodePhase={eso().loreJodePhase} jonePhase={eso().loreJonePhase} />
          </div>
          <div class="v2-moon-pop__lore">
            ESO renders both moons on a shared cycle. In the books, Jode (Masser) is the larger thirty-day moon and Jone (Secunda) is the smaller fifteen-day moon.
          </div>
          <Show when={bloodmoonActive()}>
            <div class="v2-moon-pop__warning">Bloodmoon window: werewolf and vampire NPC spawns are most likely in Bangkorai, The Rift, and Reaper's March.</div>
          </Show>
          <button class="v2-moon-pop__open" type="button" onClick={() => { wm.addWidget('eso-clock'); setOpen(false); }}>
            Open full clock widget
          </button>
        </div>
      </Show>
    </div>
  );
}

function MoonOrb(props) {
  return (
    <div class={`v2-moon-pop__orb v2-moon-pop__orb--${props.kind}`} style={{ '--lit': `${props.pct}%` }}>
      <span>{props.label}</span>
    </div>
  );
}

function MoonStat(props) {
  return (
    <div class="v2-moon-pop__stat">
      <span class="v2-moon-pop__stat-title">{props.title}</span>
      <span><b>Jode</b> {props.jode}% · {MOON_NAMES[props.jodePhase] || props.jodePhase}</span>
      <span><b>Jone</b> {props.jone}% · {MOON_NAMES[props.jonePhase] || props.jonePhase}</span>
    </div>
  );
}

